﻿import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuickLaunchListItem } from '../models/quick-launch-list-item';
import { QuickLaunchGroup } from '../models/quick-launch-group';
import { QuickSearchItemType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import { TextBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { List } from 'devextreme-react';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

export interface Props {
    onCancel?: () => void;
}

export default function QuickLaunch(props: Props) {
    const navigate = useNavigate();

    const [searchFilter, setSearchFilter] =
        useState<string>();

    const [disableSearchButton, setDisableSearchButton] =
        useState<boolean>(false);

    const [searchGroups, setSearchGroups] =
        useState<QuickLaunchGroup[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    return (
        <div>
            <div>
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <label className="textbox-label">Search</label>
                        <TextBox id="quick-launch-search-textbox" className="textbox"
                            onEnterKey={onSearchEnterKeyDown}
                            onValueChanged={searchFilterValueChanged}
                            onInput={searchFilterInputChange}
                            showClearButton={true}
                            placeholder="search product or batch"
                            value={searchFilter ? searchFilter : undefined} />
                    </div>

                    <Button id="searchButton" text="Search" icon="find" type="success"
                        className="pw-button-right-labeled-textbox"
                        hint="search"
                        height={'1.6rem'}
                        disabled={disableSearchButton}
                        onClick={onSearchClick} />
                </div>

                <div className="pw-margin-left-05 pw-margin-right-05 pw-bordered-pane">
                    <List height="28rem"
                        dataSource={searchGroups ? searchGroups : []}
                        itemRender={renderSearchItem}
                        grouped={true}
                        collapsibleGroups={true}
                        groupRender={groupTemplate} />
                </div>

            </div>

            <div className="pw-comp-row">
                <div className="pw-centered-buttons-container">
                    <Button id="cancelButton"
                        className="pw-button"
                        text="Cancel" type="normal"
                        hint="cancel"
                        height={'2.6em'}
                        onClick={onCancelClick} />
                </div>
            </div>


            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onSearchEnterKeyDown(e: any) {
        //console.log("onSearchKeyDown");
        //console.log(e);
        //console.log(e.event.key);
        //console.log(searchFilter);

        if (searchFilter)
            getSearchItems(searchFilter);
    }

    function searchFilterValueChanged(e: any) {
        setSearchFilter(e.value);
    }

    function searchFilterInputChange(e: any) {
        if (e.event && e.event.currentTarget &&
            e.event.currentTarget.value) {
            let disable = false;

            if (e.event.currentTarget.value.toString().length < 3)
                disable = true;

            setDisableSearchButton(disable);
        }
    }

    function onSearchClick() {
        if (searchFilter)
            getSearchItems(searchFilter);
    }

    function onCancelClick() {
        if (props.onCancel)
            props.onCancel();
    }

    function renderSearchItem(item: QuickLaunchListItem) {
        if (item.itemId) {
            switch (item.itemType) {
                case QuickSearchItemType.ProductPlanning:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit" id={item.itemId} onClick={goToProductPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-blue">architecture</span>
                            <div className="render-search-item-name">{`Part #: ${item.itemName}`}</div>
                            <div className="render-search-item-description">{`Vendor Part #: ${item.description}`}</div>
                        </div>
                    );

                case QuickSearchItemType.BatchPlanning:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit pw-purple" id={item.itemId} onClick={goToBatchPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-purple">architecture</span>
                            <div className="render-search-item-name">{`Batch Name: ${item.itemName}`}</div>
                            <div className="render-search-item-description">{`Batch Type: ${item.description}, Version: ${item.otherData}`}</div>
                        </div>
                    );

                case QuickSearchItemType.ProductionBenchSheet:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit pw-dark-blue" id={item.itemId} onClick={goToProductionBenchSheetPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-dark-blue">package</span>
                            <div className="render-search-item-name">{`Batch Name: ${item.itemName}`}</div>
                            <div className="render-search-item-description">{`Production Data: ${item.description}`}</div>
                        </div>
                    );

                case QuickSearchItemType.RawMaterial:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit pw-orange" id={item.itemId} onClick={goToRawMaterialsPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-orange">auto_transmission</span>
                            <div className="render-search-item-name">{item.itemName}</div>
                            <div className="render-search-item-description">{`Description: ${item.description}`}</div>
                        </div>
                    );

                case QuickSearchItemType.Calculator:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit pw-green" id={item.itemId} onClick={goToCalculatorPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-green">science</span>
                            <div className="render-search-item-name">{item.itemName}</div>
                            <div className="render-search-item-description">{item.description}</div>
                        </div>
                    );

                case QuickSearchItemType.QcBenchSheet:
                    return (
                        <div className="render-search-item-container">
                            <div className="render-search-item-links-container">
                                <div className="render-search-item-link bench-sheet-datagrid-status-icon">
                                    <i className="dx-icon-edit pw-azure" id={`${item.otherData}/${item.itemId}`} onClick={goToBenchSheetPage} />
                                </div>
                            </div>
                            <span className="material-symbols-outlined search-item-icon pw-azure">science</span>
                            <div className="render-search-item-name">{`Batch: ${item.itemName}`}</div>
                            <div className="render-search-item-description">{`Technology: ${item.description}`}</div>
                        </div>
                    );
            }
        }
    }

    function goToProductPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/product/${e.target.id}`);
            }
        }
    }

    function goToBatchPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/batch/${e.target.id}`);
            }
        }
    }

    function goToProductionBenchSheetPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/batch-production/${e.target.id}`);
            }
        }
    }

    function goToRawMaterialsPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/rawmaterials/${e.target.id}`);
            }
        }
    }

    function goToCalculatorPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/masterassaycalculator/${e.target.id}`);
            }
        }
    }

    function goToBenchSheetPage(e: any) {
        if (props.onCancel) {
            props.onCancel();

            if (e.target && e.target.id) {
                navigate(`/benchsheet/${e.target.id}`);
            }
        }
    }

    async function getSearchItems(itemSearch: string) {
        await CrudFunctions.getData(`products/productbatchesquicksearch/${itemSearch}`)
            .then(data => {
                setSearchGroups(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Search Items");
            });
    }

    function groupTemplate(item) {
        let colorCss = "pw-blue";

        switch (item.key) {
            case "Product Planning":
                colorCss = "pw-blue";
                break;
            case "Batch Planning":
                colorCss = "pw-purple";
                break;
            case "Production Bench Sheet":
                colorCss = "pw-dark-blue";
                break;
            case "Raw Materials":
                colorCss = "pw-orange";
                break;
            case "Calculator":
                colorCss = "pw-green";
                break;
            case "QC Bench Sheets":
                colorCss = "pw-azure";
                break;
        }

        const css = `pw-font-size-11 ${colorCss}`;

        return (
            <div className={css}>{item.key}</div>
        );
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}
